import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  brandingApi,
  resolveLogoSrc,
  resolveSiteBrandingId,
} from '../lib/branding';
import { organisationsApi } from '../lib/organisations';
import type { Site } from '../lib/sites';
import { cn } from '@/lib/utils';

interface SiteBrandingLogoProps {
  site: Pick<Site, 'brandingId' | 'organisationId'> | null | undefined;
  className?: string;
}

/** Site (or its organisation) branding logo for site control sections. */
export default function SiteBrandingLogo({ site, className }: SiteBrandingLogoProps) {
  const organisationId = site?.organisationId ?? null;

  const { data: organisation } = useQuery({
    queryKey: ['organisation', organisationId],
    queryFn: () => organisationsApi.getById(organisationId!),
    enabled: !!organisationId,
  });

  const brandingId = useMemo(
    () => resolveSiteBrandingId(site, organisation),
    [site, organisation],
  );

  const { data: branding } = useQuery({
    queryKey: ['branding', brandingId],
    queryFn: () => brandingApi.getById(brandingId!),
    enabled: !!brandingId,
  });

  const logoSrc = branding ? resolveLogoSrc(branding.publicLogoPath) : null;

  if (!logoSrc) {
    return null;
  }

  return (
    <img
      src={logoSrc}
      alt={branding?.name ? `${branding.name} logo` : 'Site branding'}
      title={branding?.name ?? undefined}
      className={cn('h-10 max-w-[180px] w-auto object-contain flex-shrink-0', className)}
    />
  );
}
